import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Anchor,
  Calendar,
  Users,
  Check,
  Clock,
  Copy,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharePage() {
  const { bookingId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedShareId, setSelectedShareId] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [paying, setPaying] = useState(false);

  async function load() {
    try {
      const res = await axios.get(`${API}/group/${bookingId}/public`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Group booking not found.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  function copyLink() {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied — share with your friends.");
  }

  async function payShare() {
    if (!selectedShareId) {
      toast.error("Pick which share to pay first.");
      return;
    }
    if (!form.name || !form.email) {
      toast.error("Add your name and email.");
      return;
    }
    setPaying(true);
    try {
      const res = await axios.post(`${API}/group/checkout`, {
        booking_id: bookingId,
        share_id: selectedShareId,
        name: form.name,
        email: form.email,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start checkout.");
        setPaying(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed.");
      setPaying(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <Loader2 size={32} className="text-navy animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center px-6" data-testid="share-not-found">
        <div className="max-w-md text-center bg-white border border-[color:var(--am-line)] p-12">
          <h1 className="font-serif-display text-3xl text-navy mb-4">Hmm.</h1>
          <p className="text-[color:var(--am-muted)]">{error || "We couldn't find that group booking."}</p>
          <Link to="/" className="btn-primary mt-8 inline-flex" data-testid="share-not-found-home">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const target = Number(data.group_total) || 0;
  const paid = Number(data.group_paid) || 0;
  const pct = target > 0 ? Math.min(100, Math.round((paid / target) * 100)) : 0;
  const remaining = Math.max(0, target - paid);
  const isClosed = data.group_status !== "collecting";

  return (
    <div className="min-h-screen bg-sand" data-testid="share-page">
      {/* Top nav */}
      <header className="nav-glass sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-navy" data-testid="share-logo">
            <Anchor size={22} strokeWidth={1.5} />
            <span className="font-serif-display text-2xl">Anchor Management</span>
          </Link>
          <button
            onClick={copyLink}
            className="text-xs tracking-[0.22em] uppercase text-navy hover:opacity-70 flex items-center gap-2"
            data-testid="share-copy-link"
          >
            <Copy size={14} /> Copy Link
          </button>
        </div>
      </header>

      <section className="max-w-[1200px] mx-auto px-6 lg:px-12 pt-16 pb-12">
        <div className="overline mb-5">Share a Sail</div>
        <h1 className="font-serif-display font-light text-5xl md:text-6xl text-navy leading-[1.02] max-w-3xl">
          {data.initiator_name} is rallying a charter — <em className="italic">join in.</em>
        </h1>
        <p className="text-[color:var(--am-muted)] max-w-xl mt-6 leading-relaxed">
          Below is the breakdown. Pick your share, pay your portion, and you're in. The day is locked once the whole group has paid.
        </p>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 pb-24">
        {/* Left: Charter details + shares */}
        <div className="lg:col-span-7 space-y-10">
          <div className="bg-white border border-[color:var(--am-line)] p-8">
            <div className="overline mb-4">The Charter</div>
            <h2 className="font-serif-display text-3xl text-navy mb-6">{data.experience}</h2>
            <div className="space-y-3 text-sm text-[color:var(--am-muted)]">
              <div className="flex items-start gap-3">
                <Calendar size={16} className="mt-0.5 text-navy flex-shrink-0" />
                <div>
                  {data.dates?.map((d, i) => (
                    <div key={i}>
                      {d.date} · {d.overnight ? "Overnight" : `${d.start_time} for ${d.duration_hours}h`}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users size={16} className="text-navy" />
                <div>{data.guests} guests · capacity 10</div>
              </div>
              <div className="flex items-center gap-3">
                <Clock size={16} className="text-navy" />
                <div>Charter is locked when 100% of shares are paid</div>
              </div>
            </div>
          </div>

          <div>
            <div className="overline mb-4">Pick Your Share</div>
            <div className="space-y-2">
              {data.shares.map((s) => {
                const disabled = s.paid || isClosed;
                const selected = selectedShareId === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => setSelectedShareId(s.id)}
                    className={`w-full text-left p-5 border flex items-center justify-between transition-colors ${
                      disabled
                        ? "border-[color:var(--am-line)] bg-[color:var(--am-line)]/30 cursor-not-allowed"
                        : selected
                        ? "border-navy bg-white"
                        : "border-[color:var(--am-line)] bg-white hover:border-navy/50"
                    }`}
                    data-testid={`share-option-${s.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-5 h-5 flex-shrink-0 border flex items-center justify-center ${
                          s.paid ? "bg-navy border-navy" : selected ? "bg-navy border-navy" : "border-[color:var(--am-muted)]"
                        }`}
                      >
                        {(s.paid || selected) && <Check size={13} strokeWidth={2.5} className="text-white" />}
                      </span>
                      <div>
                        <div className="font-serif-display text-xl text-navy">
                          {s.name || (s.role === "initiator" ? "Initiator" : "Open share")}
                        </div>
                        <div className="text-[0.62rem] tracking-[0.22em] uppercase text-[color:var(--am-muted)] mt-0.5">
                          {s.role} {s.paid && "· paid"}
                        </div>
                      </div>
                    </div>
                    <div className="font-serif-display text-2xl text-navy">${s.amount.toLocaleString()}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Pay form */}
          {!isClosed && (
            <div className="bg-pink-soft p-8" data-testid="share-pay-form">
              <div className="overline mb-4">Your Details</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="overline block mb-2">Your name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="rounded-none bg-white"
                    data-testid="share-form-name"
                  />
                </div>
                <div>
                  <Label className="overline block mb-2">Your email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="rounded-none bg-white"
                    data-testid="share-form-email"
                  />
                </div>
              </div>
              <button
                onClick={payShare}
                disabled={paying || !selectedShareId || !form.name || !form.email}
                className="btn-primary w-full justify-center mt-6"
                data-testid="share-pay-button"
              >
                {paying ? "Redirecting…" : "Pay My Share"} <ArrowRight size={14} />
              </button>
              <div className="text-xs text-navy/70 text-center mt-3">
                Secure payment
              </div>
            </div>
          )}

          {isClosed && data.group_status === "confirmed" && (
            <div className="bg-navy text-white p-8" data-testid="share-confirmed">
              <div className="overline text-white/70 mb-2">All shares paid</div>
              <h3 className="font-serif-display text-3xl">The charter is locked. See you at the dock.</h3>
            </div>
          )}

          {isClosed && data.group_status === "expired" && (
            <div className="bg-[color:var(--am-line)]/40 p-8" data-testid="share-expired">
              <div className="overline mb-2">Window closed</div>
              <h3 className="font-serif-display text-3xl text-navy">This group booking has expired.</h3>
              <p className="text-[color:var(--am-muted)] mt-3 text-sm">
                Not everyone paid by the deadline. Anyone who did pay will be refunded.
              </p>
            </div>
          )}
        </div>

        {/* Right: Progress sidebar */}
        <aside className="lg:col-span-5">
          <div className="sticky top-28 bg-white border border-[color:var(--am-line)] p-8" data-testid="share-progress">
            <div className="overline mb-4">Progress</div>
            <div className="font-serif-display text-5xl text-navy leading-none">
              ${paid.toLocaleString()}
              <span className="text-[color:var(--am-muted)] text-2xl"> / ${target.toLocaleString()}</span>
            </div>
            <div className="text-xs text-[color:var(--am-muted)] mt-2">{pct}% collected</div>

            <div className="mt-6 h-2 bg-[color:var(--am-line)] relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-navy transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>

            <div className="hairline my-8" />

            <ul className="space-y-3 text-sm text-[color:var(--am-muted)]">
              <li className="flex gap-2"><Check size={14} className="mt-0.5 flex-shrink-0 text-navy" /> Each person pays only their share</li>
              <li className="flex gap-2"><Check size={14} className="mt-0.5 flex-shrink-0 text-navy" /> Charter locked at 100%</li>
              <li className="flex gap-2"><Check size={14} className="mt-0.5 flex-shrink-0 text-navy" /> Auto-refund if not 100% by 7 days before sail date</li>
            </ul>

            {!isClosed && (
              <>
                <div className="hairline my-8" />
                <div className="overline mb-2">Remaining</div>
                <div className="font-serif-display text-3xl text-navy">${remaining.toLocaleString()}</div>
              </>
            )}
          </div>
        </aside>
      </section>
    </div>
  );
}
